import {
  MonitorRanking,
  priorityLabels,
} from "@p0security/shared/assessment/constants";
import { AppPaths } from "@p0security/shared/routes/constants";
import { MonitorPriority } from "@p0security/shared/types/assessment/monitor";
import { assertNever } from "@p0security/shared/types/index";
import {
  DefaultOptionType,
  Flex,
  Segmented,
  Select,
  Typography,
} from "@p0security/ui";
import { PriorityTooltip } from "components/Assessment/components/monitor/PriorityTooltip";
import { FindingsContext } from "components/Assessment/contexts/FindingsContext";
import { ScopeContext } from "components/Assessment/contexts/ScopeContext";
import { useNavigateWithEnv } from "components/Assessment/hooks/useNavigateWithEnv";
import { priorityColor } from "components/Assessment/utils/monitor";
import { orderBy } from "lodash";
import pluralize from "pluralize";
import { FilterFunc } from "rc-select/lib/Select";
import { FlattenOptionData } from "rc-select/lib/interface";
import { useCallback, useContext, useMemo, useState } from "react";
import { StyledTag } from "styles/StyledTags";
import { colors } from "styles/variables";

import { FindingsBySeverity } from "./FindingsBySeverity";
import { Pane } from "./Pane";
import { TopFindings } from "./TopFindings";
import { ColorCodeIndicator } from "./common/ColorCodeIndicator";

type PosturePanelOptions = "Open Findings" | "Top Findings";
type MonitorOption = {
  label: string;
  value: string;
  priority: MonitorPriority;
};

export const PosturePanel: React.FC<{
  isLoading: boolean;
  hasEnvironments: boolean;
}> = ({ isLoading, hasEnvironments }) => {
  const { allMonitors } = useContext(FindingsContext);
  const [selectedView, setSelectedView] =
    useState<PosturePanelOptions>("Open Findings");
  const [selectedMonitorIds, setSelectedMonitorIds] = useState<string[]>([]);

  const { scopeKey } = useContext(ScopeContext);
  const navigate = useNavigateWithEnv();
  const handleViewFindings = useCallback(() => {
    navigate(`../${AppPaths.Posture}?state=${scopeKey}`);
  }, [navigate, scopeKey]);

  const options: PosturePanelOptions[] = ["Open Findings", "Top Findings"];

  const segmentedOnChange = useCallback((value: PosturePanelOptions) => {
    setSelectedView(value);
  }, []);

  const monitorOptions = useMemo<MonitorOption[]>(
    () =>
      orderBy(
        Object.values(allMonitors),
        [(i) => MonitorRanking[i.priority], "monitorId"],
        ["asc", "asc"]
      ).map((monitor) => ({
        label: `${monitor.label} (${priorityLabels[
          monitor.priority
        ].toUpperCase()})`,
        value: monitor.monitorId,
        priority: monitor.priority,
      })),
    [allMonitors]
  );
  const filterOption = useCallback<FilterFunc<DefaultOptionType>>(
    (input, option) => {
      if (!option?.label) return false;
      return option.label
        .toString()
        .toLowerCase()
        .includes(input.toLowerCase());
    },
    []
  );

  const renderTag = useCallback(
    (props: { value: string; label: React.ReactNode }) => {
      return (
        <StyledTag color={colors.tagColors.lightGrey}>{props.label}</StyledTag>
      );
    },
    []
  );

  const renderMonitorLabel = useCallback(
    (option: FlattenOptionData<DefaultOptionType>) => {
      const monitorOption = option.data as MonitorOption;
      const { color } = priorityColor[monitorOption?.priority ?? "CRITICAL"];
      return (
        <Flex align="center">
          <ColorCodeIndicator color={color} className="h-[1em]!" />
          <Typography.Text>{option.label}</Typography.Text>
        </Flex>
      );
    },
    []
  );

  const renderFilteredMonitors = useMemo(() => {
    return (
      <Typography.Text>
        {`${selectedMonitorIds.length} ${pluralize(
          "monitor",
          selectedMonitorIds.length
        )}`}
      </Typography.Text>
    );
  }, [selectedMonitorIds]);

  const select = useMemo(() => {
    switch (selectedView) {
      case "Open Findings":
        return (
          <Select
            options={monitorOptions}
            value={selectedMonitorIds}
            onChange={setSelectedMonitorIds}
            autoClearSearchValue={false}
            filterOption={filterOption}
            mode="multiple"
            /* eslint-disable-line react/forbid-component-props */ style={{
              width: 200,
            }}
            size="middle"
            styles={{ popup: { root: { width: 500 } } }}
            allowClear
            maxTagCount={0}
            tagRender={renderTag}
            placeholder="Filter by monitor"
            optionRender={renderMonitorLabel}
            maxTagPlaceholder={renderFilteredMonitors}
          />
        );
      case "Top Findings":
        return undefined;
      default:
        assertNever(selectedView);
    }
  }, [
    filterOption,
    monitorOptions,
    renderFilteredMonitors,
    renderMonitorLabel,
    renderTag,
    selectedMonitorIds,
    selectedView,
  ]);

  const content = useMemo(() => {
    switch (selectedView) {
      case "Open Findings":
        return (
          <FindingsBySeverity
            hasEnvironments={hasEnvironments}
            isLoading={isLoading}
            selectedMonitorIds={selectedMonitorIds}
          />
        );
      case "Top Findings":
        return <TopFindings isLoading={isLoading} />;
      default:
        assertNever(selectedView);
    }
  }, [hasEnvironments, isLoading, selectedMonitorIds, selectedView]);

  return (
    <Pane
      title={<Segmented options={options} onChange={segmentedOnChange} />}
      showMore={{ text: "View in Posture", onClick: handleViewFindings }}
      tooltipPayload={<PriorityTooltip />}
      additionalControls={select}
    >
      <div style={{ height: "275px" }}>{content}</div>
    </Pane>
  );
};
